import React, { useState, useEffect } from 'react';
import { XMarkIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import AlertDialog from './AlertDialog';
import {
    HomeIcon,
    BuildingOffice2Icon,
    GlobeAsiaAustraliaIcon,
    WrenchScrewdriverIcon,
    PlusIcon,
    BanknotesIcon,
    CurrencyDollarIcon,
    ChartBarIcon,
    ShieldCheckIcon,
    KeyIcon,
    CreditCardIcon,
    DocumentTextIcon,
    ClipboardDocumentCheckIcon,
    BriefcaseIcon,
    UserGroupIcon,
    BuildingLibraryIcon,
    TruckIcon,
    ScaleIcon,
    CalculatorIcon,
    PencilSquareIcon,
    CheckCircleIcon,
    BeakerIcon,
    CubeIcon,
    HeartIcon,
    StarIcon,
    LightBulbIcon,
    AcademicCapIcon,
    BookOpenIcon,
    ChatBubbleLeftRightIcon,
    PhoneIcon,
    EnvelopeIcon,
    MapPinIcon,
    ShoppingBagIcon,
    ReceiptPercentIcon,
    PresentationChartLineIcon,
    ArrowTrendingUpIcon,
    SparklesIcon,
    RocketLaunchIcon,
    BoltIcon,
    FireIcon,
    SunIcon,
    MoonIcon,
    CloudIcon
} from '@heroicons/react/24/outline';

// Icon options
const iconOptions = [
    { name: 'HomeIcon', icon: HomeIcon, label: 'Home' },
    { name: 'BuildingOffice2Icon', icon: BuildingOffice2Icon, label: 'Building' },
    { name: 'BuildingLibraryIcon', icon: BuildingLibraryIcon, label: 'Library' },
    { name: 'BanknotesIcon', icon: BanknotesIcon, label: 'Banknotes' },
    { name: 'CurrencyDollarIcon', icon: CurrencyDollarIcon, label: 'Dollar' },
    { name: 'CreditCardIcon', icon: CreditCardIcon, label: 'Credit Card' },
    { name: 'ReceiptPercentIcon', icon: ReceiptPercentIcon, label: 'Receipt' },
    { name: 'ChartBarIcon', icon: ChartBarIcon, label: 'Chart' },
    { name: 'PresentationChartLineIcon', icon: PresentationChartLineIcon, label: 'Presentation' },
    { name: 'ArrowTrendingUpIcon', icon: ArrowTrendingUpIcon, label: 'Trending Up' },
    { name: 'CalculatorIcon', icon: CalculatorIcon, label: 'Calculator' },
    { name: 'ShieldCheckIcon', icon: ShieldCheckIcon, label: 'Shield' },
    { name: 'KeyIcon', icon: KeyIcon, label: 'Key' },
    { name: 'DocumentTextIcon', icon: DocumentTextIcon, label: 'Document' },
    { name: 'ClipboardDocumentCheckIcon', icon: ClipboardDocumentCheckIcon, label: 'Clipboard' },
    { name: 'BriefcaseIcon', icon: BriefcaseIcon, label: 'Briefcase' },
    { name: 'UserGroupIcon', icon: UserGroupIcon, label: 'Users' },
    { name: 'TruckIcon', icon: TruckIcon, label: 'Truck' },
    { name: 'ScaleIcon', icon: ScaleIcon, label: 'Scale' },
    { name: 'WrenchScrewdriverIcon', icon: WrenchScrewdriverIcon, label: 'Wrench' },
    { name: 'PencilSquareIcon', icon: PencilSquareIcon, label: 'Edit' },
    { name: 'CheckCircleIcon', icon: CheckCircleIcon, label: 'Check' },
    { name: 'BeakerIcon', icon: BeakerIcon, label: 'Beaker' },
    { name: 'CubeIcon', icon: CubeIcon, label: 'Cube' },
    { name: 'HeartIcon', icon: HeartIcon, label: 'Heart' },
    { name: 'StarIcon', icon: StarIcon, label: 'Star' },
    { name: 'LightBulbIcon', icon: LightBulbIcon, label: 'Bulb' },
    { name: 'AcademicCapIcon', icon: AcademicCapIcon, label: 'Academic' },
    { name: 'BookOpenIcon', icon: BookOpenIcon, label: 'Book' },
    { name: 'GlobeAsiaAustraliaIcon', icon: GlobeAsiaAustraliaIcon, label: 'Globe' },
    { name: 'ChatBubbleLeftRightIcon', icon: ChatBubbleLeftRightIcon, label: 'Chat' },
    { name: 'PhoneIcon', icon: PhoneIcon, label: 'Phone' },
    { name: 'EnvelopeIcon', icon: EnvelopeIcon, label: 'Email' },
    { name: 'MapPinIcon', icon: MapPinIcon, label: 'Location' },
    { name: 'ShoppingBagIcon', icon: ShoppingBagIcon, label: 'Shopping' },
    { name: 'SparklesIcon', icon: SparklesIcon, label: 'Sparkles' },
    { name: 'RocketLaunchIcon', icon: RocketLaunchIcon, label: 'Rocket' },
    { name: 'BoltIcon', icon: BoltIcon, label: 'Bolt' },
    { name: 'FireIcon', icon: FireIcon, label: 'Fire' },
    { name: 'SunIcon', icon: SunIcon, label: 'Sun' },
    { name: 'MoonIcon', icon: MoonIcon, label: 'Moon' },
    { name: 'CloudIcon', icon: CloudIcon, label: 'Cloud' },
    { name: 'PlusIcon', icon: PlusIcon, label: 'Plus' }
];

// Gradient color options
const gradientOptions = [
    { from: 'blue-500', to: 'indigo-600', label: 'Blue' },
    { from: 'green-500', to: 'emerald-600', label: 'Green' },
    { from: 'purple-500', to: 'violet-600', label: 'Purple' },
    { from: 'orange-500', to: 'red-600', label: 'Orange' },
    { from: 'teal-500', to: 'cyan-600', label: 'Teal' },
    { from: 'pink-500', to: 'rose-600', label: 'Pink' }
];

const ProductModal = ({ isOpen, onClose, onSubmit, product, loading }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        icon_name: 'HomeIcon',
        link: '',
        gradient_from: 'blue-500',
        gradient_to: 'indigo-600',
        features: [],
        display_order: 0,
        is_active: true
    });

    const [featureInput, setFeatureInput] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [dimensionAlert, setDimensionAlert] = useState({ isOpen: false, width: 0, height: 0, file: null });

    useEffect(() => {
        if (product) {
            setFormData({
                ...product,
                features: typeof product.features === 'string'
                    ? JSON.parse(product.features)
                    : product.features || []
            });

            // Set image preview if product has an image
            if (product.image_url) {
                setImagePreview(product.image_url);
            }
        } else {
            setFormData({
                title: '',
                description: '',
                icon_name: 'HomeIcon',
                link: '',
                gradient_from: 'blue-500',
                gradient_to: 'indigo-600',
                features: [],
                display_order: 0,
                is_active: true
            });
            setImageFile(null);
            setImagePreview(null);
        }
    }, [product]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAddFeature = () => {
        if (featureInput.trim()) {
            setFormData(prev => ({
                ...prev,
                features: [...prev.features, featureInput.trim()]
            }));
            setFeatureInput('');
        }
    };

    const handleRemoveFeature = (index) => {
        setFormData(prev => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index)
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.match(/^image\/(webp|png)$/)) {
                alert('Only WebP and PNG formats are allowed');
                e.target.value = '';
                return;
            }

            // Validate dimensions
            const img = new Image();
            const reader = new FileReader();

            reader.onloadend = () => {
                img.src = reader.result;
                img.onload = () => {
                    const width = img.width;
                    const height = img.height;

                    // Recommended dimensions: 4100 x 2700
                    if (width !== 4100 || height !== 2700) {
                        setDimensionAlert({
                            isOpen: true,
                            width,
                            height,
                            file
                        });
                        e.target.value = '';
                        return;
                    }

                    setImageFile(file);
                    setImagePreview(reader.result);
                };
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Create FormData for multipart/form-data submission
        const submitData = new FormData();
        submitData.append('title', formData.title);
        submitData.append('description', formData.description);
        submitData.append('icon_name', formData.icon_name);
        submitData.append('link', formData.link);
        submitData.append('gradient_from', formData.gradient_from);
        submitData.append('gradient_to', formData.gradient_to);
        submitData.append('features', JSON.stringify(formData.features));
        submitData.append('display_order', formData.display_order);
        submitData.append('is_active', formData.is_active ? '1' : '0');

        // Add image file if selected
        if (imageFile) {
            submitData.append('image', imageFile);
        }

        onSubmit(submitData);
    };

    const handleGradientSelect = (gradient) => {
        setFormData(prev => ({
            ...prev,
            gradient_from: gradient.from,
            gradient_to: gradient.to
        }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between rounded-t-3xl">
                    <h2 className="text-2xl font-bold text-slate-800">
                        {product ? 'Edit Product' : 'Add New Product'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                    >
                        <XMarkIcon className="h-6 w-6 text-slate-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-8 space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                        {/* Title */}
                        <div className="col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Product Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                placeholder="e.g., Home Construction Loan"
                            />
                        </div>

                        {/* Description */}
                        <div className="col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Description *
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                required
                                rows={3}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                                placeholder="Brief description of the product"
                            />
                        </div>

                        {/* Icon Selection */}
                        <div className="col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Icon (HeroIcons v2 Outline) *
                            </label>
                            <div className="space-y-3">
                                {/* Icon Dropdown */}
                                <select
                                    name="icon_name"
                                    value={formData.icon_name}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all appearance-none bg-white"
                                >
                                    {iconOptions.map((option) => (
                                        <option key={option.name} value={option.name}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>

                                {/* Icon Preview */}
                                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                                    <span className="text-sm font-medium text-slate-600">Preview:</span>
                                    {(() => {
                                        const selectedIcon = iconOptions.find(opt => opt.name === formData.icon_name);
                                        const Icon = selectedIcon ? selectedIcon.icon : HomeIcon;
                                        return <Icon className="h-8 w-8 text-primary-600" />;
                                    })()}
                                    <span className="text-sm text-slate-500">
                                        {iconOptions.find(opt => opt.name === formData.icon_name)?.label || 'Home'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Gradient Selection */}
                        <div className="col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-3">
                                Color Gradient *
                            </label>
                            <div className="grid grid-cols-6 gap-3">
                                {gradientOptions.map((gradient) => (
                                    <button
                                        key={gradient.label}
                                        type="button"
                                        onClick={() => handleGradientSelect(gradient)}
                                        className={`relative h-12 rounded-xl bg-gradient-to-r from-${gradient.from} to-${gradient.to} border-2 transition-all ${formData.gradient_from === gradient.from && formData.gradient_to === gradient.to
                                            ? 'border-slate-800 scale-110'
                                            : 'border-transparent hover:scale-105'
                                            }`}
                                    >
                                        <span className="sr-only">{gradient.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Product Image */}
                        <div className="col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Product Image <span className="text-slate-400">(Optional)</span>
                            </label>
                            <p className="text-xs text-slate-500 mb-3">
                                📐 Recommended: 4100 x 2700 pixels | Formats: WebP, PNG only
                            </p>
                            <div className="flex gap-4">
                                {/* Upload Area */}
                                <div className="flex-1">
                                    <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-primary-500 transition-colors">
                                        <input
                                            type="file"
                                            accept=".webp,.png,image/webp,image/png"
                                            onChange={handleImageChange}
                                            className="hidden"
                                        />
                                        <CloudArrowUpIcon className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                                        <p className="text-sm font-medium text-slate-600">Upload Image</p>
                                        <p className="text-xs text-slate-400 mt-1">WebP or PNG, max 10MB</p>
                                    </label>
                                </div>

                                {/* Image Preview */}
                                {imagePreview && (
                                    <div className="w-48 h-32 rounded-xl overflow-hidden border-2 border-slate-200">
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Link */}
                        <div className="col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Link URL
                            </label>
                            <input
                                type="url"
                                name="link"
                                value={formData.link}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                placeholder="https://example.com/product"
                            />
                        </div>

                        {/* Features */}
                        <div className="col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Features
                            </label>
                            <div className="flex gap-2 mb-3">
                                <input
                                    type="text"
                                    value={featureInput}
                                    onChange={(e) => setFeatureInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                                    className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                    placeholder="Add a feature"
                                />
                                <button
                                    type="button"
                                    onClick={handleAddFeature}
                                    className="px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
                                >
                                    Add
                                </button>
                            </div>
                            <div className="space-y-2">
                                {formData.features.map((feature, index) => (
                                    <div key={index} className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-lg">
                                        <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                                        <span className="flex-1 text-sm text-slate-700">{feature}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveFeature(index)}
                                            className="text-red-500 hover:text-red-700 text-sm font-medium"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Display Order */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Display Order
                            </label>
                            <input
                                type="number"
                                name="display_order"
                                value={formData.display_order}
                                onChange={handleChange}
                                min="0"
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            />
                        </div>

                        {/* Active Toggle */}
                        <div className="flex items-center">
                            <label className="flex items-center space-x-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleChange}
                                    className="w-5 h-5 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                                />
                                <span className="text-sm font-semibold text-slate-700">Active</span>
                            </label>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-4 pt-6 border-t border-slate-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
                        >
                            {loading ? 'Saving...' : product ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Dimension Alert Dialog */}
            <AlertDialog
                isOpen={dimensionAlert.isOpen}
                onClose={() => setDimensionAlert({ isOpen: false, width: 0, height: 0, file: null })}
                onConfirm={() => {
                    const { file } = dimensionAlert;
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        setImageFile(file);
                        setImagePreview(reader.result);
                    };
                    reader.readAsDataURL(file);
                }}
                title="Image Dimension Warning"
                message={`Image dimensions are ${dimensionAlert.width}x${dimensionAlert.height}px.\nRecommended: 4100x2700px.\n\nDo you want to continue anyway?`}
                confirmText="Continue Anyway"
            />
        </div>
    );
};

export default ProductModal;
